// cSpell:enableCompoundWords
import { TeamNamesByID } from '@shared/constants';
import { formatValueAsDate } from '@shared/helpers';
import { DESKTOP_VERTICAL_BAR_HEIGHT_SMALL } from 'app/constants';
import DonutCircle from 'app/scenes/statsmanager/components/rank/DonutCircle';
import TeamLogo from 'app/scenes/statsmanager/components/teamlogo/TeamLogo';
import { formatTeamValue } from 'helpers/ChartHelpers';
import { isMobile } from 'helpers/PlatformHelpers';
import { formatPercentage, formatPoints, formatTDs, formatYards, getNumberEnding } from 'helpers/StringHelpers';
import React, { useEffect, useState } from 'react';
import { FormatUnitType, TeamRankInfo } from 'types';
import HorizontalLine from './HorizontalLine';
import styles from './Rank.module.scss';
import VerticalLine from './VerticalLine';

export type RankOrientation = 'Horizontal' | 'Vertical';

type RankProps = {
  className?: string;
  rankInfo: TeamRankInfo;
  displayType: 'HomeTeam' | 'Leader' | 'AwayTeam';
  formatUnitType?: FormatUnitType;
  orientation?: RankOrientation;
  chartBy?: 'Rank' | 'Value';
  showUnitType?: boolean;
  showAvg?: boolean;
  direction?: string;
  rankCategory?: string;
  desktopMinHeight?: number;
};

export const getFormattedRank = (ri: TeamRankInfo) => {
  const tiedPrefix = ri.isRankTied ? 'T-' : '';
  // when team is on bye week
  if (ri.teamRank === 0 && ri.teamValue === 0) {
    return <span className={styles.ByeWeek}> {ri.bestTeam?.teamID !== '' ? 'bye' : 'TBD'}</span>;
  }

  return (
    <>
      {tiedPrefix}
      {ri.teamRank}
      <span className={styles.Superscript}>{getNumberEnding(ri.teamRank)}</span>
    </>
  );
};

export const preFormatTeamValue = (
  ri: TeamRankInfo,
  displayValue: number = 0,
  showUnitType: boolean = false,
  formatUnitType: FormatUnitType,
  rankCategory?: string,
) => {
  if (ri.teamRank === 0 && ri.teamValue === 0) return <span className={styles.ByeWeek}>---</span>;

  if (!showUnitType) return formatNumber(displayValue);

  return formatTeamValue(formatUnitType, ri, displayValue, rankCategory);
};

const formatNumber = (value: number) => {
  return Math.ceil(value) !== Math.floor(value) ? value.toFixed(1) : value;
};

const getTeamValue = (teamValue: number) => formatNumber(teamValue);

const formatBarChartTeamValue = (
  ri: TeamRankInfo,
  displayValue: number,
  showUnitType: boolean = false,
  formatUnitType: FormatUnitType = 'yards',
) => {
  if (ri.teamRank === 0 && ri.teamValue === 0) return <span className={styles.ByeWeek}>---</span>;

  if (!showUnitType) return formatNumber(displayValue);

  switch (formatUnitType) {
    case 'time':
      return formatValueAsDate(displayValue);
    case 'percent':
      return formatPercentage(displayValue);
    case 'points':
      return formatPoints(displayValue);
    case 'ratio':
      return getTeamValue(displayValue);
    case 'yards':
      return formatYards(displayValue);
    case 'tds':
      return formatTDs(displayValue);
    default:
      return getTeamValue(displayValue);
  }
};

const Rank: React.FC<RankProps> = ({
  className = '',
  rankInfo,
  displayType,
  formatUnitType = 'yards',
  orientation = 'Horizontal',
  chartBy = 'Rank',
  showUnitType = false,
  showAvg = false,
  direction,
  rankCategory,
  desktopMinHeight = DESKTOP_VERTICAL_BAR_HEIGHT_SMALL,
}) => {
  const orientationCls = styles[`Rank${orientation}Orientation`];
  const baseCls = `${styles.RankContainer} ${orientationCls} ${className}`;

  const [, setDivisor] = useState<number>(1);

  const [displayValue, setDisplayValue] = useState<number>(0);

  useEffect(() => {
    setDisplayValue(showAvg ? rankInfo.teamAvg : rankInfo.teamValue);
  }, [rankInfo.teamAvg, rankInfo.teamValue, showAvg]);

  useEffect(() => {
    if (rankInfo.valueDivisor) {
      setDivisor(rankInfo.valueDivisor);
    } else {
      setDivisor(1);
    }
  }, [rankInfo.valueDivisor]);

  if (isMobile()) {
    return (
      <div className={`${styles.RankContainer} ${className}`}>
        {displayType === 'Leader' && (
          <TeamLogo teamName={TeamNamesByID[parseInt(rankInfo.teamID)]} className={`${styles.TeamLogoSize}`} />
        )}
        <DonutCircle
          rankInfo={rankInfo}
          displayType={displayType}
          chartBy={chartBy}
          donutLabel={<span>{getFormattedRank(rankInfo)}</span>}
          donutSubLabel={
            <span>{preFormatTeamValue(rankInfo, displayValue, showUnitType, formatUnitType, rankCategory)}</span>
          }
        />
      </div>
    );
  }

  return (
    <div className={baseCls}>
      {displayType === 'Leader' && (
        <TeamLogo teamName={TeamNamesByID[parseInt(rankInfo.teamID)]} className={`${styles.TeamLogoSize}`} />
      )}
      {/* {orientation !== 'Horizontal' && <span className={`${styles.RankNumber}`}>{getFormattedRank(rankInfo)}</span>} */}

      {orientation === 'Horizontal' && (
        <HorizontalLine
          rankInfo={rankInfo}
          displayType={displayType}
          chartBy={chartBy}
          side={direction}
          startLabel={<span className={`${styles.RankNumber}`}>{getFormattedRank(rankInfo)}</span>}
          endLabel={
            <span className={`${styles.RankValue}`}>
              {formatBarChartTeamValue(rankInfo, displayValue, showUnitType, formatUnitType)}
            </span>
          }
        />
      )}

      {orientation === 'Vertical' && (
        <VerticalLine
          rankInfo={rankInfo}
          displayType={displayType}
          chartBy={chartBy}
          desktopMinHeight={desktopMinHeight}
          startLabel={<span className={`${styles.RankNumber}`}>{getFormattedRank(rankInfo)}</span>}
          endLabel={
            <span className={`${styles.RankValue}`}>
              {formatBarChartTeamValue(rankInfo, displayValue, showUnitType, formatUnitType)}
            </span>
          }
        />
      )}

      {/* {orientation !== 'Horizontal' && <span className={`${styles.RankValue}`}>{formatTeamValue(rankInfo)}</span>} */}
    </div>
  );
};

export default Rank;
